import React, { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Paper,
} from '@mui/material';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  ArrowPathIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';

import { API_URL } from '../../constants/urls';

interface OrganizationStripeStatus {
  id: string;
  name: string;
  plan_type: string;
  billing_type: string;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  subscription_status: string | null;
  stripe_status?: {
    customer_exists: boolean;
    subscription_exists: boolean;
    customer_error?: string;
    subscription_error?: string;
    stripe_plan?: string;
    stripe_status?: string;
  };
}

interface StripeVerificationResult {
  success: boolean;
  customer_exists: boolean;
  action: string;
  message: string;
  error?: string;
  organization_state: {
    id: string;
    name: string;
    plan_type: string;
    billing_type: string;
    stripe_customer_id: string | null;
    stripe_subscription_id: string | null;
    subscription_status: string | null;
  };
}

export const StripeSyncManager: React.FC = () => {
  const [organizations, setOrganizations] = useState<
    OrganizationStripeStatus[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [verifying, setVerifying] = useState<string | null>(null);
  const [resetDialog, setResetDialog] = useState<{
    open: boolean;
    orgId: string;
    orgName: string;
  }>({ open: false, orgId: '', orgName: '' });

  const fetchOrganizationsWithStripeData = async () => {
    setLoading(true);
    setError(null);
    try {
      // First get all organizations
      const response = await axios.get(`${API_URL}/admin/organizations`);
      const orgs = response.data;

      // Filter to only those with Stripe data
      const stripeOrgs = orgs.filter(
        (org: any) =>
          org.stripe_customer_id ||
          org.stripe_subscription_id ||
          org.plan_type !== 'free',
      );

      // Initialize stripe_status for display
      const orgsWithStatus = stripeOrgs.map((org: any) => ({
        ...org,
        stripe_status: org.stripe_customer_id
          ? {
              customer_exists: true, // Assume true until verified
              subscription_exists: !!org.stripe_subscription_id,
            }
          : undefined,
      }));

      setOrganizations(orgsWithStatus);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch organizations');
    } finally {
      setLoading(false);
    }
  };

  const verifyOrganization = async (orgId: string) => {
    setVerifying(orgId);
    setError(null);
    setSuccess(null);

    try {
      const response = await axios.post(
        `${API_URL}/admin/organizations/${orgId}/verify-stripe-customer`,
      );

      const result: StripeVerificationResult = response.data;

      // Update the organization in our list with the verification result
      setOrganizations((prev) =>
        prev.map((org) => {
          if (org.id === orgId) {
            return {
              ...org,
              ...result.organization_state,
              stripe_status: {
                customer_exists: result.customer_exists,
                subscription_exists:
                  !!result.organization_state.stripe_subscription_id,
                customer_error: result.error,
              },
            };
          }
          return org;
        }),
      );

      if (result.action === 'reset') {
        setSuccess(
          `Fixed: ${result.organization_state.name} - Customer didn't exist in Stripe, reset to free plan`,
        );
      } else {
        setSuccess(
          `Verified: ${result.organization_state.name} - ${result.message}`,
        );
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to verify organization');
    } finally {
      setVerifying(null);
    }
  };

  const resetOrganization = async () => {
    const { orgId, orgName } = resetDialog;
    setError(null);
    setSuccess(null);

    try {
      await axios.post(
        `${API_URL}/admin/organizations/${orgId}/reset-stripe-billing`,
      );

      setSuccess(`Successfully reset Stripe billing for ${orgName}`);
      setResetDialog({ open: false, orgId: '', orgName: '' });

      // Refresh the organization list
      await fetchOrganizationsWithStripeData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to reset organization');
    }
  };

  const getStatusIcon = (org: OrganizationStripeStatus) => {
    if (!org.stripe_status) {
      return <ExclamationTriangleIcon className="w-5 h-5 text-gray-400" />;
    }

    if (
      org.stripe_status.customer_exists &&
      (!org.stripe_subscription_id || org.stripe_status.subscription_exists)
    ) {
      return <CheckCircleIcon className="w-5 h-5 text-green-600" />;
    }

    return <XCircleIcon className="w-5 h-5 text-red-600" />;
  };

  const getStatusChip = (org: OrganizationStripeStatus) => {
    if (!org.stripe_customer_id && org.plan_type === 'free') {
      return <Chip label="No Stripe Data" size="small" />;
    }

    if (!org.stripe_status) {
      return <Chip label="Not Verified" size="small" color="default" />;
    }

    if (org.stripe_status.customer_error) {
      return <Chip label="Customer Missing" size="small" color="error" />;
    }

    if (org.stripe_status.customer_exists) {
      return <Chip label="Customer Exists" size="small" color="success" />;
    }

    return <Chip label="Unknown" size="small" />;
  };

  return (
    <Card>
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h6">Stripe Synchronization Manager</Typography>
          <Button
            variant="contained"
            startIcon={<ArrowPathIcon className="w-4 h-4" />}
            onClick={fetchOrganizationsWithStripeData}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Load Organizations'}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            onClose={() => setSuccess(null)}
            sx={{ mb: 2 }}
          >
            {success}
          </Alert>
        )}

        {organizations.length === 0 && !loading && (
          <Alert severity="info">
            No organizations with Stripe data found. Click &quot;Load
            Organizations&quot; to fetch data.
          </Alert>
        )}

        {organizations.length > 0 && (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Organization</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell>Stripe Customer ID</TableCell>
                  <TableCell>Subscription ID</TableCell>
                  <TableCell>DB Status</TableCell>
                  <TableCell>Stripe Check</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {organizations.map((org) => (
                  <TableRow key={org.id}>
                    <TableCell>{getStatusIcon(org)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{org.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {org.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={org.plan_type}
                        size="small"
                        color={org.plan_type === 'free' ? 'default' : 'primary'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: 'monospace' }}
                      >
                        {org.stripe_customer_id || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: 'monospace' }}
                      >
                        {org.stripe_subscription_id || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {org.subscription_status ? (
                        <Chip
                          label={org.subscription_status}
                          size="small"
                          color={
                            org.subscription_status === 'active'
                              ? 'success'
                              : 'default'
                          }
                        />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>{getStatusChip(org)}</TableCell>
                    <TableCell align="right">
                      {verifying === org.id ? (
                        <CircularProgress size={20} />
                      ) : (
                        <Box display="flex" gap={1} justifyContent="flex-end">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => verifyOrganization(org.id)}
                            disabled={verifying !== null}
                          >
                            Verify & Fix
                          </Button>
                          {org.stripe_customer_id && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<TrashIcon className="w-3 h-3" />}
                              onClick={() =>
                                setResetDialog({
                                  open: true,
                                  orgId: org.id,
                                  orgName: org.name,
                                })
                              }
                            >
                              Reset
                            </Button>
                          )}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box mt={3}>
          <Alert severity="info">
            <Typography variant="subtitle2" gutterBottom>
              How this tool works:
            </Typography>
            <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
              <li>
                <strong>Verify & Fix:</strong> Checks if the Stripe customer
                exists. If not, it automatically resets the organization to a
                free plan.
              </li>
              <li>
                <strong>Reset:</strong> Completely clears all Stripe data and
                sets the organization to a free plan. Use this to start fresh.
              </li>
              <li>
                <strong>Status Icons:</strong> ✓ = Synced, ✗ = Mismatch, ⚠ =
                Not verified
              </li>
            </Typography>
          </Alert>
        </Box>
      </CardContent>

      {/* Reset Confirmation Dialog */}
      <Dialog
        open={resetDialog.open}
        onClose={() => setResetDialog({ open: false, orgId: '', orgName: '' })}
      >
        <DialogTitle>Reset Stripe Billing?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to reset Stripe billing for{' '}
            <strong>{resetDialog.orgName}</strong>?
          </DialogContentText>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This will:
            <ul>
              <li>Clear all Stripe customer and subscription IDs</li>
              <li>Set the organization to the free plan</li>
              <li>Allow them to start fresh with Stripe</li>
            </ul>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setResetDialog({ open: false, orgId: '', orgName: '' })
            }
          >
            Cancel
          </Button>
          <Button onClick={resetOrganization} color="error" variant="contained">
            Reset Stripe Billing
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};
